import { getSpecialProductByKey } from './api';
import {
  parseReferralPayload,
  readPromoCache,
  readReferralFromSearch,
} from './deeplink';

/** 路由 name → 默认 link_key（可被 URL ?link_key= / code 内 _k_ 覆盖） */
export const ROUTE_LINK_KEY = {
  Home: 'shop_mall',
  Trxtk: 'energy_refund',
  EnergyUse: 'energy_rental',
  TgMenber: 'tg_member',
  TrxExchange: 'trx_exchange',
  PhoneCode: 'phone_code',
  SgkPage: 'sgk',
  TudouDb: 'tudou_db',
  WalletActityTo: 'energy_whitelist',
  XinbiActity: 'xinbi_actity',
  TokenTY: 'token_ty',
  WalletActity: 'wallet_actity',
  ExpressDelivery: 'express',
  ExpressDeliveryDetail: 'express',
  MiningEarnings: 'mining',
  HighFreqBomber: 'high_freq_bomber',
  CardSecretBuy: 'card_secret_buy',
  AccountMall: 'account_mall',
  TrxEnergyPlatform: 'trx_energy_platform',
  NetSms: 'netsms',
  PickupOrder: 'pickup1',
  ReturnOrder: 'return1',
  FansStore: 'fans_store',
  FansStoreDetail: 'fans_store',
};

/** link_key 与 ExpressDelivery 页面复用关系 */
export const EXPRESS_DELIVERY_LINK_KEYS = ['express', 'register', 'pickup'];

const DEFAULT_PRODUCT = {
  title: '',
  price: 0,
  count: 0,
  sell: 0,
  src: '',
  sendWay: '',
  detail: '',
};

/** 页面展示用标题（去掉 TG 链接前缀 emoji，保留正文） */
export function formatSpecialTitle(title) {
  if (!title) return '';
  const cleaned = String(title)
    .replace(/^(?:🔗|♻|📦|📤|⚡|🔋|💎|✅|🔰|\s)+/u, '')
    .trim();
  return cleaned || title;
}

export function resolveLinkKey(route) {
  // 1) 旧链 ?link_key=
  const fromQuery =
    route?.query?.link_key ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('link_key')
      : null);
  if (fromQuery) return String(Array.isArray(fromQuery) ? fromQuery[0] : fromQuery);

  // 2) 单参数 code / referral_code 内嵌 _k_linkKey
  const codeRaw =
    route?.query?.code ||
    route?.query?.referral_code ||
    route?.query?.r ||
    '';
  const code = Array.isArray(codeRaw) ? codeRaw[0] : codeRaw;
  if (code) {
    const fromPayload = parseReferralPayload(String(code)).linkKey;
    if (fromPayload) return fromPayload;
  }
  if (typeof window !== 'undefined') {
    const fromSearch = readReferralFromSearch(window.location.search).linkKey;
    if (fromSearch) return fromSearch;
    const pRaw = route?.query?.p;
    const p = Array.isArray(pRaw) ? pRaw[0] : pRaw;
    const fromPromo = p ? readPromoCache(String(p)) : null;
    if (fromPromo?.linkKey) return fromPromo.linkKey;
  }

  // 3) 路由默认
  const name = route?.name;
  if (name && ROUTE_LINK_KEY[name]) return ROUTE_LINK_KEY[name];
  return null;
}

/** 已上架特殊商品是否包含价格配置（含 0） */
export function hasConfiguredPrice(product) {
  return product?.configured === true;
}

/** 特殊商品有配置时用其价格（含 0），否则保留页面默认价 */
export function applySpecialProductPrice(product, fallbackPrice) {
  return hasConfiguredPrice(product) ? Number(product.price) : fallbackPrice;
}

/**
 * 按 link_key 拉取特殊商品，合并默认值
 * @returns {Promise<object>}
 */
export async function loadSpecialProduct(linkKey, fallback = {}) {
  if (!linkKey) return { ...DEFAULT_PRODUCT, ...fallback };
  try {
    const res = await getSpecialProductByKey(linkKey);
    const p = res?.data;
    if (!p || p.status === '0') {
      return { ...DEFAULT_PRODUCT, ...fallback, link_key: linkKey, configured: false };
    }
    return {
      title: formatSpecialTitle(p.title) || p.title || fallback.title || '',
      rawTitle: p.title || '',
      tgTitle: p.tg_title || p.title || '',
      price: p.price ?? fallback.price ?? 0,
      count: p.count ?? fallback.count ?? 0,
      sell: p.sell ?? fallback.sell ?? 0,
      src: p.src || fallback.src || '',
      sendWay: p.sendWay || fallback.sendWay || '',
      detail: p.detail || fallback.detail || '',
      link_key: p.link_key || linkKey,
      link_path: p.link_path || '',
      product_type: p.product_type,
      configured: true,
    };
  } catch (e) {
    console.warn('[specialProduct] load failed:', linkKey, e);
    return { ...DEFAULT_PRODUCT, ...fallback, link_key: linkKey, configured: false };
  }
}

/**
 * 在页面 mounted 中调用，写入 vm.specialProduct 并可选合并到 detail 对象
 */
export async function initSpecialProductPage(vm, linkKey, options = {}) {
  const key = linkKey || resolveLinkKey(vm.$route);
  if (!key) return null;

  const product = await loadSpecialProduct(key, options.fallback || {});

  if ('specialProduct' in vm) {
    vm.specialProduct = product;
  } else if (vm.$data && 'specialProduct' in vm.$data) {
    vm.specialProduct = product;
  }

  if (options.titleField && product.title) {
    vm[options.titleField] = product.title;
  }

  if (options.detailField && vm[options.detailField]) {
    Object.assign(vm[options.detailField], {
      title: product.rawTitle || product.title || vm[options.detailField].title,
      price: hasConfiguredPrice(product)
        ? product.price
        : (product.price ?? vm[options.detailField].price),
      count: product.count ?? vm[options.detailField].count,
      sell: product.sell ?? vm[options.detailField].sell,
      src: product.src || vm[options.detailField].src,
      sendWay: product.sendWay || vm[options.detailField].sendWay,
      detail: product.detail || vm[options.detailField].detail,
    });
  }

  if (options.subtitleField && product.detail) {
    vm[options.subtitleField] = product.detail;
  }

  options.onLoaded?.(product);
  return product;
}
